import { api } from "./client";
import type { ApiResponse } from "./types";

export interface Festival {
  id: string;
  code: string;
  slug: string;
  name: string;
  nameHindi: string | null;
  nameRegional: Record<string, unknown> | null;
  year: number;
  /** A civil date sent as midnight UTC: read the date part only. */
  festivalDate: string;
  festivalEndDate?: string | null;
  significance: string | null;
  religion?: string | null;
  tradition?: string | null;
  observanceType?: string | null;
  colorTheme?: string | null;
  isNational?: boolean;
  isGazettedHoliday?: boolean;
  isLunarBased?: boolean;
  isCanary?: boolean;
}

/** This browser's IANA zone: "today" is the reader's date, never the server's. */
export const readerTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

export function getTodayFestivals(timeZone: string = readerTimeZone()) {
  return api.get<ApiResponse<{ items: Festival[]; today: string; timeZone: string }>>(
    "/utsav/today",
    { timeZone },
  );
}

export function getUpcomingFestivals(params?: { days?: number; limit?: number; timeZone?: string }) {
  return api.get<ApiResponse<{ items: Festival[]; range: { from: string; to: string }; timeZone: string }>>(
    "/utsav/upcoming",
    { timeZone: readerTimeZone(), ...params },
  );
}

export function getFestivalCalendar(params?: {
  year?: number;
  religion?: string;
  tradition?: string;
}) {
  return api.get<ApiResponse<{ items: Festival[]; year: number }>>(
    "/utsav/calendar",
    params,
  );
}

export interface FestivalTradition {
  id: string;
  traditionKey: string;
  traditionName: string;
  description: string;
  states: string[];
  traditionalFoods: string[];
  greeting: string | null;
}

export interface FestivalInstance {
  id: string;
  code: string;
  year: number;
  festivalDate: string;
  festivalEndDate: string | null;
}

/** The closest instance with its traditions, and every year held for the slug. */
export function getFestivalBySlug(slug: string) {
  return api.get<ApiResponse<{
    festival: Festival & { traditions: FestivalTradition[] };
    instances: FestivalInstance[];
  }>>(`/utsav/festival/${slug}`);
}
